import type { DoseSpotLaunchErrorCode } from '@behuman/core-contract'

/**
 * A launch refusal, ready to render. There is **one mint-only endpoint and no
 * eligibility query**, so an un-provisioned prescriber discovers it by clicking:
 * every one of these is a primary state of the Prescribe modal, not an edge case.
 */
export type DoseSpotLaunchFailure = {
  code: DoseSpotLaunchErrorCode | 'UNKNOWN'
  title: string
  /**
   * What went wrong, in a sentence. The middleware writes these to be shown as-is
   * (they carry the field list, the administrator path, DoseSpot's own account
   * wording), so the browser prefers the server's message and only falls back to
   * the per-code default when there isn't one.
   */
  message: string
  /** Chart-side field labels, so the fixable states can name what to fix. */
  fields: string[]
  /**
   * DoseSpot's own reason per field, parallel to `fields` — both come from the keys
   * and values of one `ModelState` object, so they share an order. Empty unless
   * DoseSpot rejected the demographics.
   */
  messages: string[]
  /** DoseSpot's own words, shown only where they add something (account problems). */
  detail?: string
  /** Label for the "open the patient editor" action; absent when that isn't the fix. */
  fixLabel?: string
  /** Whether re-minting could plausibly succeed without anything else changing. */
  retryable: boolean
}

type CodeCopy = {
  title: string
  /** Used only when the server sent no message (a bare status, a proxy in between). */
  fallback: string
  fixLabel?: string
  retryable: boolean
}

/**
 * Per-code presentation. The *body* copy lives in the middleware (it is the same
 * sentence in a log-free, user-facing form and it carries the specifics); what is
 * authored here is the heading and which actions the state offers.
 */
const COPY: Record<DoseSpotLaunchErrorCode, CodeCopy> = {
  PATIENT_INCOMPLETE: {
    title: 'DoseSpot needs a few more patient details',
    fallback: "DoseSpot needs more of this patient's details before you can prescribe.",
    fixLabel: 'Add details',
    // The edit loop *is* the retry: nothing on DoseSpot's side would change.
    retryable: false,
  },
  PATIENT_REJECTED: {
    title: 'DoseSpot rejected this patient record',
    fallback: "DoseSpot rejected this patient's details.",
    fixLabel: 'Fix patient details',
    retryable: true,
  },
  PRESCRIBER_NOT_MAPPED: {
    title: "Prescribing isn't set up for your account",
    fallback:
      "Ask a BeHuman administrator to link your account — it's a one-time setup, and prescribing works immediately after.",
    retryable: false,
  },
  PRESCRIBER_ACCOUNT: {
    title: "There's a problem with your DoseSpot account",
    fallback: 'DoseSpot would not start a prescribing session for your account.',
    retryable: false,
  },
  NOT_CONFIGURED: {
    title: "Prescribing isn't set up for this organization",
    fallback: 'Prescribing is not set up for this organization yet.',
    retryable: false,
  },
  PATIENT_NOT_FOUND: {
    title: "This patient can't be opened in DoseSpot",
    fallback: 'This patient could not be opened.',
    retryable: false,
  },
  DOSESPOT_UNAVAILABLE: {
    title: 'DoseSpot is unavailable',
    fallback: 'DoseSpot could not be reached. Please try again in a moment.',
    retryable: true,
  },
}

const UNKNOWN: CodeCopy = {
  title: "Couldn't open DoseSpot",
  fallback: 'Something went wrong opening the prescribing window. Please try again.',
  retryable: true,
}

/**
 * Decodes whatever the launch call threw into a renderable state.
 *
 * Read duck-typed rather than through `instanceof ORPCError`: oRPC's own type
 * definition warns that the check does not survive bundle boundaries, and the
 * existing enrichment hook already hit that. A network failure or an unrecognised
 * code lands on the retryable unknown state — never on a data or account problem,
 * because nothing was learned about either.
 */
export function describeDoseSpotLaunchFailure(error: unknown): DoseSpotLaunchFailure {
  const raw = error != null && typeof error === 'object' ? (error as Record<string, unknown>) : {}
  const rawCode = typeof raw['code'] === 'string' ? raw['code'] : ''
  const code = rawCode in COPY ? (rawCode as DoseSpotLaunchErrorCode) : null
  const copy = code ? COPY[code] : UNKNOWN
  const message = typeof raw['message'] === 'string' && raw['message'] ? raw['message'] : ''
  const data = raw['data'] != null && typeof raw['data'] === 'object' ? raw['data'] : {}
  const { fields, messages, detail } = data as {
    fields?: unknown
    messages?: unknown
    detail?: unknown
  }

  return {
    code: code ?? 'UNKNOWN',
    title: copy.title,
    message: message || copy.fallback,
    fields: stringList(fields),
    messages: stringList(messages),
    ...(typeof detail === 'string' && detail && { detail }),
    ...(copy.fixLabel && { fixLabel: copy.fixLabel }),
    retryable: copy.retryable,
  }
}

const stringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
